import { DashboardDto } from "./generated/api-client";

/** The one entity a dashboard is about, or undefined if it spans several. */
export function dashboardEntity(dashboard: DashboardDto): string | undefined {
  const touched = new Set<string>();

  (dashboard.dataTables ?? []).forEach((table) => {
    if (table.collection) touched.add(table.collection);
  });
  (dashboard.charts ?? []).forEach((chart) => {
    (chart.collections ?? []).forEach((name) => {
      if (name) touched.add(name);
    });
  });
  (dashboard.forms ?? []).forEach((form) => {
    (form.fields ?? []).forEach((field: { collection?: string }) => {
      if (field?.collection) touched.add(field.collection);
    });
  });

  if (touched.size !== 1) return undefined;
  // Array.from, not [...set]: the project targets ES5 without
  // downlevelIteration, so spreading a Set is a tsc error.
  return Array.from(touched)[0];
}

/**
 * True when the collection route already owns this dashboard's entity.
 *
 * The generator emits one page per entity, and the collection route draws that
 * entity's charts as well as its grid, so a single-entity dashboard is a second
 * door onto a room the rail already lists: the same table, the same figures,
 * ordered the other way round, with nothing in the label to say which is which.
 *
 * Superseding is only safe when the collection route can actually be reached,
 * so the entity has to be present in `collectionNames`. A dashboard that spans
 * several entities, or names one the analyse did not return, has no equivalent
 * collection route.
 *
 * The rail hides these links; the dashboard route redirects them. Both ask this
 * one question so they cannot disagree about which pages still exist.
 */
export function supersededByCollection(
  dashboard: DashboardDto | undefined,
  collectionNames: Iterable<string>,
): string | undefined {
  if (!dashboard) return undefined;
  const entity = dashboardEntity(dashboard);
  if (!entity) return undefined;
  return new Set(collectionNames).has(entity) ? entity : undefined;
}

/**
 * Dashboards that still deserve their own nav anchor.
 *
 * Five of five demo dashboards are single-entity twins of a collection the rail
 * already lists, so all five drop out.
 */
export function navDashboards<T extends DashboardDto>(
  dashboards: T[] | undefined,
  collectionNames: Iterable<string>,
): T[] {
  const known = new Set(collectionNames);
  return (dashboards ?? []).filter(
    (dashboard) => !supersededByCollection(dashboard, known),
  );
}
